import { EllipsisVerticalIcon, LinkIcon } from "@heroicons/react/24/outline";
import SuperscriptIcon from "@mui/icons-material/Superscript";
import {
    Autocomplete,
    Box,
    Button,
    createFilterOptions,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import clsx from "clsx";
import { debounce } from "lodash";
import { nanoid } from "nanoid";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { DataTableCell, DataTableFootnote, DataType } from "../../../../../../Models";
import { addCellFootnote, clearCellFootnote, setCellContent } from "../../../../../../context/DataTableReducer";
import useDataTableContext from "../../../../../../context/useDataTableContext";
import { getUsedFootnotes } from "../../../../../../helpers";
import EditableContent from "../EditableContent";

export interface DataTableCellProps {
    row: string;
    cell: DataTableCell;
    visible: boolean;
    readOnly: boolean;
}

const EditableCell = ({ row, cell, visible, readOnly }: DataTableCellProps) => {
    const { state, dispatch } = useDataTableContext();
    const { columns, rows, footnotes, disciplines, refreshCount } = state;

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLDivElement | null>(null);
    const [focus, setFocus] = useState<boolean>(false);
    const [hover, setHover] = useState<boolean>(false);

    useEffect(() => {
        const inputElement = inputRef.current;
        const wrapperElement = wrapperRef.current;

        if (inputElement) {
            inputElement.addEventListener("focus", handleFocus);
            inputElement.addEventListener("blur", handleBlur);

            if (cell.focus) {
                inputElement.focus();
                inputElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
            }
        }

        if (wrapperElement) {
            wrapperElement.addEventListener("mouseenter", handleMouseEnter);
            wrapperElement.addEventListener("mouseleave", handleMouseLeave);
        }

        return () => {
            if (inputElement) {
                inputElement.removeEventListener("focus", handleFocus);
                inputElement.removeEventListener("blur", handleBlur);
            }

            if (wrapperElement) {
                wrapperElement.removeEventListener("mouseenter", handleMouseEnter);
                wrapperElement.removeEventListener("mouseleave", handleMouseLeave);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFocus = () => setFocus(true);
    const handleBlur = () => setFocus(false);
    const handleMouseEnter = () => setHover(true);
    const handleMouseLeave = () => setHover(false);

    const { id, value } = useMemo(() => cell, [cell]);
    const [cellValue, setCellValue] = useState<string>(value);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const column = useMemo(() => rows.find((x) => x.id === row)!.cells.findIndex((x) => x.id === id), [cell.id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cellDataType = useMemo(() => columns[column].dataType, [column]);

    useEffect(() => {
        if (refreshCount === 0 || !inputRef.current) return;
        if (cellValue === value && cellValue === inputRef.current.innerText) return;

        inputRef.current.innerHTML = value;
        setCellValue(value);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshCount]);

    const onChange = (value: string | null) => dispatch(setCellContent(row, id, value || ""));
    const onChangeDebounced = debounce((value: string) => onChange(value), 500);

    const renderFootnotes = () => {
        const usedFootnotes = getUsedFootnotes(rows, footnotes, readOnly);
        return cell.footnotes.map((x) => usedFootnotes.findIndex((y) => y.id === x) + 1).join(", ");
    };

    const renderGidValue = (value: string | Array<string>) => {
        if (Array.isArray(cell.metadata?.disciplines)) {
            if (!disciplines || disciplines.length === 0) return "-";
            const attachedDisciplines = cell.metadata.disciplines;

            return (
                <div>
                    {attachedDisciplines.map((disciplineId, index) => {
                        const discipline = disciplines.find((x) => x.id === disciplineId) ?? { code: "UNKNOWN" };

                        return (
                            <Typography key={index} variant={"body2"} component={"p"}>
                                {discipline.code}
                            </Typography>
                        );
                    })}
                </div>
            );
        }

        return (
            <Typography
                variant={"body2"}
                component={"span"}
                sx={{ flex: 1, p: 0, m: 2, minWidth: 50, minHeight: 20, display: "inline-block" }}
                suppressContentEditableWarning={true}
                dangerouslySetInnerHTML={{ __html: value as string }}
            />
        );
    };

    return (
        <td
            scope={"row"}
            className={clsx([
                "py-3 px-2.5 border-l border-b border-[#E5E5E5] font-medium",
                "group-last:first:rounded-bl-lg group-last:last:rounded-br-lg",
                cellDataType === DataType.BOOLEAN && "text-center",
            ])}
        >
            <div className={clsx(["flex items-center", visible ? "opacity-100" : "opacity-30"])} ref={wrapperRef}>
                {cellDataType === DataType.STRING && (
                    <Typography
                        id={id}
                        variant={"body2"}
                        component={EditableContent}
                        contentEditable={!readOnly}
                        ref={inputRef}
                        sx={{ flex: 1, p: 0, m: 0, minWidth: 50, minHeight: 20, display: "inline-block" }}
                        suppressContentEditableWarning={true}
                        onInput={(e) => onChangeDebounced(e.currentTarget.innerHTML)}
                        dangerouslySetInnerHTML={{ __html: cellValue }}
                    />
                )}

                {cellDataType === DataType.BOOLEAN && renderGidValue(value)}

                <EditableCellMenu row={row} cell={cell} focus={focus} hover={hover} />
                {cell.footnotes && cell.footnotes?.length > 0 && (
                    <div className="pl-1 pt-1 mb-auto ml-auto">
                        <sup style={{}}>{renderFootnotes()}</sup>
                    </div>
                )}
            </div>
        </td>
    );
};

export default EditableCell;

interface EditableCellMenuProps {
    row: string;
    cell: DataTableCell;
    focus: boolean;
    hover: boolean;
}

const EditableCellMenu = ({ row, cell, focus, hover }: EditableCellMenuProps) => {
    const { dispatch } = useDataTableContext();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openFootnote, setOpenFootnote] = useState<boolean>(false);

    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    let style = {};
    if (!focus && !hover && !open) style = { opacity: 0, pointerEvents: "none" };

    const handleAddFootnote = () => {
        setOpenFootnote(true);
    };

    const handleClearFootnote = () => {
        dispatch(clearCellFootnote(row, cell.id));
        handleClose();
    };

    const onAddFootnote = (footnote: DataTableFootnoteEditor) => {
        dispatch(addCellFootnote(row, cell.id, footnote));
        setOpenFootnote(false);
    };

    return (
        <Box sx={{ ml: "auto", ...style }}>
            <IconButton
                size="small"
                tabIndex={-1}
                aria-controls={open ? "long-menu" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                sx={{ mr: 1 }}
            >
                <EllipsisVerticalIcon className="h-5 w-5" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            overflow: "visible",
                            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                            mt: 1.5,
                        },
                    },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                <MenuItem onClick={() => handleAddFootnote()}>
                    <ListItemIcon>
                        <SuperscriptIcon className="h-5 w-5" />
                    </ListItemIcon>
                    Add Footnote
                </MenuItem>
                {cell.footnotes && cell.footnotes?.length > 0 && (
                    <MenuItem onClick={() => handleClearFootnote()}>
                        <ListItemIcon>
                            <SuperscriptIcon className="h-5 w-5" />
                        </ListItemIcon>
                        Clear Footnote
                    </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleClose} disabled>
                    <ListItemIcon>
                        <LinkIcon className="h-5 w-5" />
                    </ListItemIcon>
                    Add External Link
                </MenuItem>
                <MenuItem onClick={handleClose} disabled>
                    <ListItemIcon>
                        <LinkIcon className="h-5 w-5" />
                    </ListItemIcon>
                    Add Internal Link
                </MenuItem>
            </Menu>

            <FootnoteModal
                open={openFootnote}
                onClose={() => setOpenFootnote(false)}
                onSubmit={(value) => onAddFootnote(value)}
            />
        </Box>
    );
};

interface DataTableFootnoteEditor extends DataTableFootnote {
    inputValue?: string;
}

const filter = createFilterOptions<DataTableFootnoteEditor>();

interface FootnoteModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (footnote: DataTableFootnoteEditor) => void;
}

const FootnoteModal = ({ open, onClose, onSubmit }: FootnoteModalProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const { state } = useDataTableContext();
    const { footnotes } = state;

    const [value, setValue] = useState<DataTableFootnoteEditor | null>(null);

    useEffect(() => {
        if (!open) return;
        setValue(null);
    }, [open]);

    const handleClose = () => {
        onClose();
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!value) return;
        onSubmit(value);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullScreen={fullScreen} maxWidth={"md"} fullWidth={true}>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Add Footnote</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText>Add a new footnote or selecting an existing one.</DialogContentText>
                    <Autocomplete
                        fullWidth
                        value={value}
                        onChange={(_, newValue) => {
                            if (typeof newValue === "string") {
                                const existing = footnotes.find((x) => x.value === newValue);
                                if (existing) {
                                    setValue(existing);
                                    return;
                                }

                                const parsed = { id: nanoid(), value: newValue, notetype: "manual-note" };
                                footnotes.push(parsed);
                                setValue(parsed);
                            } else if (newValue && newValue.inputValue) {
                                const parsed = { ...newValue, inputValue: undefined };
                                footnotes.push(parsed);
                                setValue(parsed);
                            } else {
                                setValue(newValue);
                            }
                        }}
                        filterOptions={(options, params) => {
                            const filtered = filter(options, params);

                            if (
                                params.inputValue !== "" &&
                                filtered.findIndex((x) => x.value === params.inputValue) === -1
                            ) {
                                filtered.push({
                                    id: nanoid(),
                                    value: params.inputValue,
                                    inputValue: `Add "${params.inputValue}"`,
                                    notetype: "manual-note",
                                });
                            }

                            return filtered;
                        }}
                        id="footnotes"
                        options={footnotes as Array<DataTableFootnoteEditor>}
                        getOptionLabel={(option) => {
                            // e.g value selected with enter, right from the input
                            if (typeof option === "string") {
                                return option;
                            }

                            if (option.inputValue) {
                                return option.inputValue;
                            }

                            return option.value;
                        }}
                        autoComplete
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                {option.inputValue ?? option.value}
                            </li>
                        )}
                        freeSolo
                        renderInput={(params) => <TextField {...params} autoFocus />}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button type="submit">Add</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
